import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { Notification } from 'apis/notifications';
import { searchApi } from 'apis/search';
import { AuthorsById } from 'components/notifications/components/notificationsPage/utils/transformNotification';

/**
 * Resolves the mention authors referenced by a list of notifications into a
 * `Map<authorId, User>`, backed by a single Search API call filtered to the
 * distinct author IDs. The query is skipped when there are no mention
 * notifications. Returns an empty map until the lookup resolves.
 */
export const useMentionAuthorLookup = (
  notifications: readonly Notification[],
): AuthorsById => {
  const authorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const n of notifications) {
      if (n.payload_data?.type === 'mention:user_mentioned') {
        ids.add(n.payload_data.params.author_id);
      }
    }
    return Array.from(ids);
  }, [notifications]);

  const { usersById } = searchApi.useReadSearchUsersQuery(
    {
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'id',
            operator: 'is_one',
            values: authorIds.map((value) => ({ value })),
          },
        ],
      },
      pageSize: authorIds.length || 1,
    },
    {
      skip: authorIds.length === 0,
      selectFromResult: ({ data }) => ({
        usersById: new Map<string, User>(
          (data?.data.results ?? []).map(({ result }) => [result.id, result]),
        ),
      }),
    },
  );

  return usersById;
};
